import { Component } from 'react'
import styled from '@emotion/styled'
import { Modal, TextArea, Box, Button, ModalTitle, EmulationBox } from 'stylewhere/components'
import {
  AppStore,
  Emulation,
  isNewEmulationFormat,
  isValidNewEmulationFormat,
  parseNewEmulationFormat
} from 'core/shared'
import { T, __ } from 'stylewhere/i18n'
import { askUserConfirmation } from 'stylewhere/shared/utils'

interface Props {
  title: string
  isOpen: boolean
  onConfirm: (input?: string) => void
  onClose: () => void
  style?: any
}

interface State {
  input?: string
  defaultValue?: string
  defaultValueCounter?: number
  savedEmulationsList: Emulation[]
}

const MODAL_HEIGHT = 400

export class EmulationModal extends Component<Props, State> {
  state: State = {
    savedEmulationsList: [...AppStore.emulationList]
  }

  advancedEmulationExample = async () => {
    const { input, defaultValueCounter } = this.state

    if (input && input.trim() !== '') {
      const proceed = await askUserConfirmation('Warning', 'Do you want to clear the current emulation?', 'No', 'Yes')
      if (!proceed) return
    }
    const emulationText = `{epc:20200202,tid:202200202,uid:UID2},\n{epc:EPC2,tid:TID2,uid:UID2}`
    this.setState({
      defaultValue: emulationText,
      defaultValueCounter: defaultValueCounter ? defaultValueCounter + 1 : 1
    })
  }

  addToFavorite = async (name: string, key?: number) => {
    const label = prompt('Insert favorite name', '')
    if (!label) return

    const newEmulations = this.state.savedEmulationsList

    const index = key ? newEmulations.findIndex((e) => e.key === key) : newEmulations.findIndex((e) => e.name === name)
    if (index >= 0) {
      newEmulations[index].favorite = true
      newEmulations[index].label = label

      await AppStore.saveEmulationList(newEmulations)
      this.setState({ savedEmulationsList: newEmulations })
    }
  }

  removeToFavorite = async (name: string, key?: number) => {
    const newEmulations = this.state.savedEmulationsList

    const index = key ? newEmulations.findIndex((e) => e.key === key) : newEmulations.findIndex((e) => e.name === name)
    if (index >= 0) {
      newEmulations[index].favorite = false
      newEmulations[index].label = undefined

      await AppStore.saveEmulationList(newEmulations)
      this.setState({ savedEmulationsList: newEmulations })
    }
  }

  deleteEmulate = async (name: string, key?: number) => {
    const { savedEmulationsList } = this.state
    // use name to use data saved with old format
    const filteredEmulations: Emulation[] = key
      ? savedEmulationsList.filter((em) => em.key !== key)
      : savedEmulationsList.filter((em) => em.name !== name)
    await AppStore.saveEmulationList(filteredEmulations)
    this.setState({ savedEmulationsList: filteredEmulations })
  }

  onSubmit = async (text?: string, confirm: boolean = true) => {
    const { savedEmulationsList, input, defaultValueCounter } = this.state
    const emulationText = text ?? input
    const isNewFormat = isNewEmulationFormat(emulationText)

    // send tags to emulate
    if (confirm) {
      //find if exist same emulation
      const index = savedEmulationsList.findIndex((f) => f.value === emulationText)
      if (index !== -1) {
        savedEmulationsList[index].date = new Date().getTime()
        await AppStore.saveEmulationList(savedEmulationsList)
        this.setState({ savedEmulationsList: savedEmulationsList })
      } else {
        // add emulation to history
        let historyName = ''
        if (isNewFormat) {
          const emulatedTags = parseNewEmulationFormat(emulationText)
          const uhf = emulatedTags.filter((t) => t.epc)
          const nfc = emulatedTags.filter((t) => t.uid)
          if (uhf.length > 0) historyName += uhf.length + ' x UHF'
          if (nfc.length > 0) historyName += (historyName !== '' ? ' + ' : '') + nfc.length + ' x NFC'
        } else {
          const codes: string[] = String(emulationText).replace(/ /g, '').split(',') ?? []
          historyName = codes.length + ' x ' + AppStore.emulationIdentifierType
        }
        const time = new Date().getTime()
        const newEmulations = [...savedEmulationsList].concat({
          key: time,
          name: historyName,
          value: emulationText,
          favorite: false,
          date: time
        })
        await AppStore.saveEmulationList(newEmulations)
        this.setState({ savedEmulationsList: newEmulations })
      }

      if (isNewFormat) {
        this.props.onConfirm(String(emulationText).trim())
      } else {
        const cleaned = String(emulationText)
          .trim()
          .replace(/ /g, '')
          .replace(/"/g, '')
          .replace(/'/g, '')
          .replace(/;/g, ',')
          .replace(/,,/g, ',')
          .replace(/\n\r/g, ',')
          .replace(/\n/g, ',')
          .replace(/\t/g, ',')
          .trim()
        this.props.onConfirm(cleaned)
      }
    } else {
      this.setState({
        input: emulationText,
        defaultValue: emulationText,
        defaultValueCounter: defaultValueCounter ? defaultValueCounter + 1 : 1
      })
    }
  }

  render() {
    const { isOpen, onClose, title } = this.props
    const { savedEmulationsList, input, defaultValue, defaultValueCounter } = this.state

    const isNewFormat = input ? isNewEmulationFormat(input ?? '') : false
    const rowsNewFormat = isNewFormat ? parseNewEmulationFormat(input ?? '') : []
    const isNewFormatValid = isNewFormat ? isValidNewEmulationFormat(input ?? '') : true

    const hintText = 'Press [CTRL+E] to show again'

    const favoriteEmulation = savedEmulationsList
      .filter((e) => e.favorite)
      .sort(function (a, b) {
        return a.date > b.date ? -1 : 1
      })
    const historyEmulation = savedEmulationsList
      .filter((e) => !e.favorite)
      .sort(function (a, b) {
        return a.date > b.date ? -1 : 1
      })
    return (
      <Modal visible={isOpen} onClose={onClose} size="4xl">
        <ModalTitle title={title} />
        <Box row>
          <Box mt={20} style={{ position: 'relative' }} height={MODAL_HEIGHT} flex={10}>
            <TextArea
              overrideValue={[defaultValue, defaultValueCounter]}
              placeholder={__(T.messages.paste_comma_separeted_tags)}
              onChange={(newValue) => this.setState({ input: newValue })}
              startFocus
              textAreaStyle={{ height: MODAL_HEIGHT, resize: 'none' }}
              onEnter={(_value, event) => {
                if (input && isNewFormatValid) {
                  this.onSubmit()
                  if (event) event.preventDefault()
                }
              }}
            />
            <Box row style={{ position: 'absolute', right: 15, bottom: 20 }}>
              {input && (
                <Button
                  title={__(T.misc.emulate)}
                  onClick={() => this.onSubmit()}
                  style={{
                    whiteSpace: 'nowrap',
                    marginLeft: 15
                  }}
                  size="small"
                  disabled={!input || !isNewFormatValid}
                />
              )}
              {!input && (
                <Button
                  title={__(T.misc.advanced_emulation)}
                  onClick={this.advancedEmulationExample}
                  style={{
                    whiteSpace: 'nowrap'
                  }}
                  variant="default"
                  size="small"
                />
              )}
            </Box>
          </Box>
          {(historyEmulation.length > 0 || favoriteEmulation.length > 0) && (
            <Box flex width={260} mt={20} pl={15} height={MODAL_HEIGHT}>
              {favoriteEmulation.length > 0 && (
                <EmulationBox
                  title={__(T.misc.emulation_favorites)}
                  list={favoriteEmulation}
                  onSubmit={this.onSubmit}
                  onDelete={this.deleteEmulate}
                  onFavorite={this.removeToFavorite}
                  mb={historyEmulation.length > 0 ? 10 : 0}
                />
              )}
              {historyEmulation.length > 0 && (
                <EmulationBox
                  title={__(T.misc.emulation_history)}
                  list={historyEmulation}
                  onSubmit={this.onSubmit}
                  onDelete={this.deleteEmulate}
                  onFavorite={this.addToFavorite}
                />
              )}
            </Box>
          )}
        </Box>

        <Box row mt={10} vcenter flex>
          <Button variant="default" title="Hide Emulation" onClick={onClose} size="small" />
          <Hint>{hintText}</Hint>
          {isNewFormat && (
            <StatusBox>
              <Status
                title={rowsNewFormat
                  .map(
                    (v) =>
                      `${v.epc ? ` epc: ${v.epc}` : ''}${v.tid ? ` tid: ${v.tid}` : ''}${v.uid ? ` uid: ${v.uid}` : ''}${v.ndef ? ` ndef: ${v.ndef}` : ''}`
                  )
                  .join(';\n')}
                isError={!isNewFormatValid}
              >
                {rowsNewFormat.length}
              </Status>
            </StatusBox>
          )}
        </Box>
      </Modal>
    )
  }
}

const StatusBox = styled.span`
  text-align: right;
  margin-left: 15px;
  flex: 1;
`
const Status = styled.span<{ isError?: boolean }>`
  background: ${({ isError }) => (isError ? '#F27D7D' : '#75EBA8')};
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
`

const Hint = styled.span`
  font-size: 19px;
  font-weight: 400;
  text-align: center;
  color: rgba(51, 51, 51, 0.52);
  margin-left: 15px;
`

const SavedListTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 10px;
`
